import { Navigate, useLocation } from "react-router-dom";
import { useAuth, getHomePath, routePermissions, type Permission } from "@/contexts/AuthContext";
import { Loader2, ShieldX } from "lucide-react";
import { Button } from "@/components/ui/button";

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isBootstrapping, permissionsReady, hasPermission, apiUser, logout } = useAuth();
  const location = useLocation();

  // While the session is being restored from the cookie on page load, don't
  // redirect — wait for rehydration to finish, otherwise a reload bounces an
  // already-logged-in user to /login.
  if (isBootstrapping) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Wait for /me/permissions so custom roles aren't denied against an empty fallback map.
  if (!permissionsReady) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const basePath = "/" + (location.pathname.split("/")[1] || "");
  const requiredPerm = routePermissions[basePath] as Permission | undefined;

  if (requiredPerm && !hasPermission(requiredPerm)) {
    const home = getHomePath(hasPermission, apiUser?.roles ?? []);
    const homeBase = "/" + (home.split("/")[1] || "");
    // No other permitted page — stay here with a clear message (avoid redirect loop).
    if (homeBase === basePath) {
      return (
        <div className="flex flex-col items-center justify-center h-[60vh] gap-4 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            <ShieldX className="h-8 w-8 text-destructive" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">Access Denied</h2>
          <p className="text-sm text-muted-foreground max-w-md">
            Your role doesn't have permission to access any pages. Contact your administrator for access.
          </p>
          <Button variant="outline" size="sm" onClick={() => logout()}>
            Sign out
          </Button>
        </div>
      );
    }
    return <Navigate to={home} replace />;
  }

  return <>{children}</>;
}
